import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ChatMessage, ChatSession, SessionContext } from '@kundaliai/shared';
import { api } from '@/lib/api';
import { generateSessionId } from '@/lib/utils';
import { useLanguageStore } from './languageStore';

interface ChatState {
  // Current session
  currentSessionId: string | null;
  messages: ChatMessage[];
  context: SessionContext | null;
  isLoading: boolean;
  error: string | null;
  creditsRemaining: number | null;

  // Session list
  sessions: ChatSession[];

  // Actions
  initSession: () => void;
  /** `retried` is internal: set on the one automatic resend after a 403. */
  sendMessage: (content: string, retried?: boolean) => Promise<void>;
  loadSession: (sessionId: string) => Promise<void>;
  loadSessions: () => Promise<void>;
  clearSession: () => void;
  deleteSession: (sessionId: string) => Promise<void>;
  setCredits: (credits: number) => void;
  refreshCredits: () => Promise<void>;
}

export const useChatStore = create<ChatState>()(
  persist(
    (set, get) => ({
      currentSessionId: null,
      messages: [],
      context: null,
      isLoading: false,
      error: null,
      creditsRemaining: null,
      sessions: [],

      initSession: () => {
        const sessionId = generateSessionId();
        set({
          currentSessionId: sessionId,
          messages: [],
          context: null,
          error: null,
        });
      },

      sendMessage: async (content: string, retried = false) => {
        const state = get();
        let sessionId = state.currentSessionId;

        if (!sessionId) {
          sessionId = generateSessionId();
          set({ currentSessionId: sessionId });
        }

        // Add user message optimistically
        const userMessage: ChatMessage = {
          id: Date.now(),
          sessionId: 0,
          role: 'user',
          content,
          metadata: null,
          createdAt: new Date(),
        };

        set((s) => ({
          messages: [...s.messages, userMessage],
          isLoading: true,
          error: null,
        }));

        try {
          // Capture user's current date/time/timezone from browser
          const now = new Date();
          const pad = (n: number) => n.toString().padStart(2, '0');
          const offsetMin = -now.getTimezoneOffset();
          const offsetSign = offsetMin >= 0 ? '+' : '-';
          const offsetH = pad(Math.floor(Math.abs(offsetMin) / 60));
          const offsetM = pad(Math.abs(offsetMin) % 60);

          const language = useLanguageStore.getState().language;

          const response = await api.sendMessage({
            sessionId,
            message: content,
            clientDateTime: {
              date: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`,
              time: `${pad(now.getHours())}:${pad(now.getMinutes())}`,
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
              utcOffset: `${offsetSign}${offsetH}:${offsetM}`,
            },
            language,
          });

          if (response.success && response.data) {
            const { userMessage, assistantMessage, credits } = response.data;

            // Update credits remaining from server response
            if (credits && credits.creditsRemaining !== undefined) {
              set({ creditsRemaining: credits.creditsRemaining });
            }

            // Validate messages before adding
            if (userMessage?.content && assistantMessage?.content) {
              set((s) => ({
                messages: [
                  ...s.messages.slice(0, -1), // Remove optimistic message
                  userMessage,
                  assistantMessage,
                ],
                isLoading: false,
              }));
            } else {
              console.error('Invalid message response:', response.data);
              set({
                error: 'Received invalid message from server',
                isLoading: false,
              });
            }
          } else {
            // Detect credits exhausted error from backend
            const errorCode = (response.error as { code?: string })?.code;

            // The stored session id belongs to another account (shared device, or a
            // sign-in that never ran sign-out). Its messages aren't this user's either:
            // start a clean conversation and resend once. `retried` stops a loop.
            if (errorCode === 'FORBIDDEN' && !retried) {
              set({ currentSessionId: generateSessionId(), messages: [], context: null, error: null });
              return get().sendMessage(content, true);
            }

            if (errorCode === 'CREDITS_EXHAUSTED') {
              set({ creditsRemaining: 0 });
            }

            set((s) => ({
              ...s,
              messages: s.messages.slice(0, -1), // Remove optimistic message
              error: response.error?.message || 'Failed to send message',
              isLoading: false,
            }));
          }
        } catch (error) {
          console.error('Send message error:', error);
          set({
            error: 'Failed to send message',
            isLoading: false,
          });
        }
      },

      loadSession: async (sessionId: string) => {
        set({ isLoading: true, error: null });

        try {
          const response = await api.getChatSession(sessionId);

          if (response.success && response.data) {
            set({
              currentSessionId: sessionId,
              messages: response.data.messages,
              context: response.data.context,
              isLoading: false,
            });
          } else {
            set({
              error: response.error?.message || 'Failed to load session',
              isLoading: false,
            });
          }
        } catch (error) {
          set({
            error: 'Failed to load session',
            isLoading: false,
          });
        }
      },

      loadSessions: async () => {
        try {
          const response = await api.getChatSessions();

          if (response.success && response.data) {
            set({ sessions: response.data });
          }
        } catch (error) {
          console.error('Failed to load sessions:', error);
        }
      },

      clearSession: () => {
        set({
          currentSessionId: null,
          messages: [],
          context: null,
          error: null,
        });
      },

      setCredits: (credits: number) => {
        set({ creditsRemaining: credits });
      },

      refreshCredits: async () => {
        const res = await api.getCreditsBalance();
        if (res.success && res.data) {
          set({ creditsRemaining: res.data.available });
        }
      },

      deleteSession: async (sessionId: string) => {
        try {
          await api.deleteChatSession(sessionId);
          set((s) => ({
            sessions: s.sessions.filter((sess) => sess.sessionId !== sessionId),
          }));

          if (get().currentSessionId === sessionId) {
            get().clearSession();
          }
        } catch (error) {
          console.error('Failed to delete session:', error);
        }
      },
    }),
    {
      name: 'kundaliai-chat',
      partialize: (state) => ({
        currentSessionId: state.currentSessionId,
        messages: state.messages.slice(-50), // Keep last 50 messages
      }),
    }
  )
);
